import 'leaflet/dist/leaflet.css';

import L from 'leaflet';
import { memo, type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { BULK_DEVICE_PAGE_SIZE } from '../constants/pagination.js';
import { useDevices, useIntl } from '../hooks';
import { DeviceStatus } from '../types/device-status';
import { DeviceType } from '../types/device-type';
import { SuppressionMode } from '../types/suppression-mode';

const DEFAULT_CENTER: [number, number] = [48.3794, 31.1656];
const DEFAULT_ZOOM = 6;
const SECTOR_RADIUS = 40_000; // meters
const SECTOR_ANGLE = 40; // degrees
const DETECTOR_CIRCLE_RADIUS = 7_500; // meters (15 km diameter)
const SUNFLOWER_CIRCLE_RADIUS = 3_500; // meters (7 km diameter)
const SUNFLOWER_DIRECTIONAL_RADIUS_US = 10_000; // meters — directional sunflower on US amplifier
const SUNFLOWER_DIRECTIONAL_RADIUS_CN = 7_000; // meters — directional sunflower on CN amplifier (model suffix -C)

interface MapViewProps {
  isActive?: boolean;
  selectedDeviceId?: string | null;
  showAllDevices?: boolean;
  onSelectedPopupClose?: () => void;
}

// Leaflet fires 'popupclose' for MapView's own closes too (marker rebuild, the auto-close in
// map.openPopup, map teardown); the flag marks them so only an operator's close is reported.
const closePopupsQuietly = (isClosingPopupsRef: RefObject<boolean>, close: () => void) => {
  const wasClosingPopups = isClosingPopupsRef.current;
  isClosingPopupsRef.current = true;
  try {
    close();
  } finally {
    isClosingPopupsRef.current = wasClosingPopups;
  }
};

// Base marker styles
const BASE_MARKER_STYLE: Partial<L.CircleMarkerOptions> = {
  radius: 4,
  weight: 2,
  opacity: 1,
  fillOpacity: 0.8,
};

export const LABEL_MIN_ZOOM = 14;
export const HIT_MARKER_RADIUS = 22; // 44px CSS diameter tap target

export const shouldShowMarkerLabel = (zoom: number, isSelected: boolean): boolean =>
  isSelected || zoom >= LABEL_MIN_ZOOM;

export const getMarkerLabelClassName = (isSelected: boolean): string =>
  isSelected ? 'map-serial-label map-serial-label--selected' : 'map-serial-label';

// Nearest-center hit resolution: among candidates whose center falls
// within hitRadius of the click point, the click belongs to whichever center is closest —
// never to whichever invisible hit circle happens to be painted on top. Ties keep the
// first candidate in array order.
export const resolveNearestDevice = (
  clickPoint: { x: number; y: number },
  candidates: Array<{ id: string; point: { x: number; y: number } }>,
  hitRadius: number,
): string | null => {
  let winnerId: string | null = null;
  let winnerDistance = Infinity;

  for (const candidate of candidates) {
    const distance = Math.hypot(candidate.point.x - clickPoint.x, candidate.point.y - clickPoint.y);
    if (distance <= hitRadius && distance < winnerDistance) {
      winnerId = candidate.id;
      winnerDistance = distance;
    }
  }

  return winnerId;
};

const HIT_AREA_STYLE: L.CircleMarkerOptions = {
  radius: HIT_MARKER_RADIUS,
  stroke: false,
  fill: true,
  fillOpacity: 0, // invisible; SVG pointer-events still hit-test the fill
  interactive: true,
};

// Status-based marker styles
export const getMarkerStyleByStatus = (
  status: DeviceStatus,
  isSelected: boolean = false,
  isDetector: boolean = false,
): L.CircleMarkerOptions => {
  const baseStyle = { ...BASE_MARKER_STYLE };

  // Set colors based on status
  switch (status) {
    case DeviceStatus.ONLINE:
      if (isDetector) {
        baseStyle.color = '#eab308'; // yellow-500
        baseStyle.fillColor = '#ca8a04'; // yellow-600
      } else {
        baseStyle.color = '#22c55e'; // green-500
        baseStyle.fillColor = '#16a34a'; // green-600
      }
      break;
    case DeviceStatus.OFFLINE:
    case DeviceStatus.UNKNOWN:
    default:
      baseStyle.color = '#ef4444'; // red-500
      baseStyle.fillColor = '#dc2626'; // red-600
      break;
  }

  // Enhance selected markers
  if (isSelected) {
    baseStyle.radius = 12;
    baseStyle.weight = 3;
    baseStyle.color = '#1d4ed8'; // blue-700 border for selected
    baseStyle.opacity = 1;
    baseStyle.fillOpacity = 0.9;
  }

  return baseStyle as L.CircleMarkerOptions;
};

// A sunflower draws a directional sector when an azimuth is set, otherwise a circle.
// Directionality is derived from azimuth presence — there is no separate backend field.
// Type predicate so the caller's azimuth narrows to `number` inside the sector branch.
export const isDirectionalAzimuth = (azimuth: number | null | undefined): azimuth is number =>
  azimuth != null;

// Chinese amplifier models carry a `-C` suffix (e.g. SUNFLOWER-1.0-C); all others are US.
// Duplicated in `backend/src/devices/device-model.utils.ts`, which declares the SAPIENT cone range
// from the same distinction.
export const isChineseAmplifier = (model: string | null | undefined): boolean =>
  (model ?? '').trim().toUpperCase().endsWith('-C');

export const getDirectionalSunflowerRadius = (model: string | null | undefined): number =>
  isChineseAmplifier(model) ? SUNFLOWER_DIRECTIONAL_RADIUS_CN : SUNFLOWER_DIRECTIONAL_RADIUS_US;

// Create sector polygon based on center point, azimuth, and radius
const createSectorPolygon = (
  center: L.LatLng,
  azimuth: number,
  radius: number,
  angle: number,
): L.LatLng[] => {
  const points: L.LatLng[] = [center];
  const startAngle = azimuth - angle / 2;
  const endAngle = azimuth + angle / 2;
  const steps = 20;

  for (let i = 0; i <= steps; i++) {
    const currentAngle = startAngle + (endAngle - startAngle) * (i / steps);
    const radians = (currentAngle * Math.PI) / 180;

    // Calculate destination point using Haversine formula
    const earthRadius = 6371000; // meters
    const lat1 = (center.lat * Math.PI) / 180;
    const lng1 = (center.lng * Math.PI) / 180;
    const angularDistance = radius / earthRadius;

    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(angularDistance) +
        Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(radians),
    );

    const lng2 =
      lng1 +
      Math.atan2(
        Math.sin(radians) * Math.sin(angularDistance) * Math.cos(lat1),
        Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2),
      );

    points.push(L.latLng((lat2 * 180) / Math.PI, (lng2 * 180) / Math.PI));
  }

  points.push(center);
  return points;
};

export default memo(function MapView({
  isActive = false,
  selectedDeviceId = null,
  showAllDevices = false,
  onSelectedPopupClose,
}: MapViewProps) {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.CircleMarker>>({});
  const dotMarkersRef = useRef<Record<string, L.CircleMarker>>({});
  const sectorsRef = useRef<Record<string, L.Polygon>>({});
  const circlesRef = useRef<Record<string, L.Circle>>({});
  const popupsRef = useRef<Record<string, L.Popup>>({});
  const openPopupDeviceIdRef = useRef<string | null>(null);
  const isClosingPopupsRef = useRef(false);
  const { data: devicesPage } = useDevices({ pageSize: BULK_DEVICE_PAGE_SIZE });
  const devices = useMemo(() => devicesPage?.devices ?? [], [devicesPage]);
  const hasInitializedViewRef = useRef(false);
  const prevSelectedDeviceIdRef = useRef<string | null | undefined>(undefined);
  const prevShowAllDevicesRef = useRef<boolean | undefined>(undefined);

  // Popups are decoupled from Leaflet's marker-bound click→toggle (which mis-attributes
  // clicks to whichever hit circle is topmost) — this is the single place a popup opens.
  // `map.openPopup` auto-closes whatever was previously open before this assignment runs,
  // so the ref must be set AFTER opening, not before.
  const openDevicePopup = useCallback((deviceId: string) => {
    const map = mapRef.current;
    const popup = popupsRef.current[deviceId];
    if (!map || !popup) {
      return;
    }
    closePopupsQuietly(isClosingPopupsRef, () => map.openPopup(popup));
    openPopupDeviceIdRef.current = deviceId;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) {
      return;
    }

    mapRef.current = L.map(container).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(mapRef.current);

    const map = mapRef.current;
    const syncLabelVisibility = () => {
      container.classList.toggle('labels-visible', shouldShowMarkerLabel(map.getZoom(), false));
    };
    map.on('zoomend', syncLabelVisibility);
    syncLabelVisibility();

    map.on('popupclose', () => {
      openPopupDeviceIdRef.current = null;
    });

    const resizeObserver = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      closePopupsQuietly(isClosingPopupsRef, () => mapRef.current?.remove());
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    // Remember which popup was open before rebuilding markers — read before teardown closes
    // it below (closing fires 'popupclose', which nulls openPopupDeviceIdRef.current).
    const openPopupDeviceId = openPopupDeviceIdRef.current;

    // Clear existing markers, sectors, circles, and popups
    Object.values(markersRef.current).forEach((marker) => marker.remove());
    Object.values(dotMarkersRef.current).forEach((marker) => marker.remove());
    Object.values(sectorsRef.current).forEach((sector) => sector.remove());
    Object.values(circlesRef.current).forEach((circle) => circle.remove());
    closePopupsQuietly(isClosingPopupsRef, () =>
      Object.values(popupsRef.current).forEach((popup) => popup.remove()),
    );
    markersRef.current = {};
    dotMarkersRef.current = {};
    sectorsRef.current = {};
    circlesRef.current = {};
    popupsRef.current = {};

    const selectionChanged = selectedDeviceId !== prevSelectedDeviceIdRef.current;
    const showAllChanged = showAllDevices !== prevShowAllDevicesRef.current;
    prevSelectedDeviceIdRef.current = selectedDeviceId;
    prevShowAllDevicesRef.current = showAllDevices;

    const markerLocations: [number, number][] = [];

    // Computed once per rebuild (not per click): every click handler below re-resolves against
    // this same list, since the clicked hit-circle is never trusted as the answer on its own.
    const devicesWithCoords: Array<{ id: string; latitude: number; longitude: number }> = [];
    for (const candidateDevice of devices) {
      const candidateLatitude = candidateDevice.settings?.latitude;
      const candidateLongitude = candidateDevice.settings?.longitude;
      if (candidateLatitude == null || candidateLongitude == null) {
        continue;
      }
      devicesWithCoords.push({
        id: candidateDevice.id,
        latitude: candidateLatitude,
        longitude: candidateLongitude,
      });
    }

    devices.forEach((device) => {
      const latitude = device.settings?.latitude;
      const longitude = device.settings?.longitude;
      const azimuth = device.settings?.azimuth;

      if (latitude == null || longitude == null) {
        return;
      }

      const isSelected = device.id === selectedDeviceId;
      const isDetector = device.settings?.deviceType === DeviceType.DETECTOR;
      const isSunflower = device.capabilities?.includes('broadband_suppressions') ?? false;
      const isDeviceInDetectorMode =
        isDetector ||
        device.settings.suppressionMode === SuppressionMode.DISABLED ||
        !device.txEnabled;
      const markerStyle = getMarkerStyleByStatus(
        device.status as DeviceStatus,
        isSelected,
        isDeviceInDetectorMode,
      );

      const dotMarker = L.circleMarker([latitude, longitude], {
        ...markerStyle,
        interactive: false,
      }).addTo(map);
      const hitMarker = L.circleMarker([latitude, longitude], HIT_AREA_STYLE).addTo(map);

      const areaColor =
        device.status === DeviceStatus.ONLINE
          ? isDeviceInDetectorMode
            ? '#eab308' // yellow-500
            : '#22c55e' // green-500
          : '#ef4444'; // red-500

      if (device.settings?.deviceType === DeviceType.DETECTOR) {
        circlesRef.current[device.id] = L.circle([latitude, longitude], {
          radius: DETECTOR_CIRCLE_RADIUS,
          color: areaColor,
          fillColor: areaColor,
          fillOpacity: 0.15,
          weight: 2,
          opacity: 0.6,
          interactive: false,
        }).addTo(map);
      } else if (isSunflower) {
        // A sunflower's `broadband_suppressions` capability is mutually exclusive with a
        // detector's `detections` (capabilities are family-derived in zahrada
        // deriveDeviceModel), so the detector branch above never steals a sunflower —
        // unless deviceType=DETECTOR is force-set on a sunflower via direct API, in which
        // case detector rendering wins by design.
        //
        // Reuse `areaColor` (not a status-only color) so the coverage area tracks the
        // same suppression/TX semantics as the marker: green when suppression is active,
        // yellow when suppression is disabled or TX is off, red when offline.
        //
        // Directionality is derived from azimuth presence: a set azimuth means the operator
        // chose directional coverage (sector), null/undefined means circular.
        if (isDirectionalAzimuth(azimuth)) {
          const center = L.latLng(latitude, longitude);
          const sectorPoints = createSectorPolygon(
            center,
            azimuth,
            getDirectionalSunflowerRadius(device.model),
            SECTOR_ANGLE,
          );

          sectorsRef.current[device.id] = L.polygon(sectorPoints, {
            color: areaColor,
            fillColor: areaColor,
            fillOpacity: 0.2,
            weight: 2,
            opacity: 0.6,
            interactive: false,
          }).addTo(map);
        } else {
          circlesRef.current[device.id] = L.circle([latitude, longitude], {
            radius: SUNFLOWER_CIRCLE_RADIUS,
            color: areaColor,
            fillColor: areaColor,
            fillOpacity: 0.15,
            weight: 2,
            opacity: 0.6,
            interactive: false,
          }).addTo(map);
        }
      } else if (azimuth !== undefined && azimuth !== null) {
        // Draw sector for directional devices
        const center = L.latLng(latitude, longitude);
        const sectorPoints = createSectorPolygon(center, azimuth, SECTOR_RADIUS, SECTOR_ANGLE);

        sectorsRef.current[device.id] = L.polygon(sectorPoints, {
          color: areaColor,
          fillColor: areaColor,
          fillOpacity: 0.2,
          weight: 2,
          opacity: 0.6,
          interactive: false,
        }).addTo(map);
      }

      // Create status badge for popup
      const statusColor = device.status === DeviceStatus.ONLINE ? '#22c55e' : '#ef4444';
      const statusText = intl.formatMessage({
        id: `map.status.${device.status.toLowerCase()}`,
      });

      const popupContent = `
        <div style="min-width: 200px;">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
            <strong style="font-size: 16px;">${device.serial}</strong>
            <span style="
              background-color: ${statusColor}; 
              color: white; 
              padding: 2px 8px; 
              border-radius: 12px; 
              font-size: 12px; 
              font-weight: bold;
            ">${statusText}</span>
          </div>
          <div style="font-size: 14px; line-height: 1.4;">
            <div><strong>${intl.formatMessage({ id: 'map.tx' })}:</strong> ${device.txEnabled ? intl.formatMessage({ id: 'map.enabled' }) : intl.formatMessage({ id: 'map.disabled' })}</div>
            <div><strong>${intl.formatMessage({ id: 'map.coordinates' })}:</strong> ${latitude.toFixed(6)}, ${longitude.toFixed(6)}</div>
            ${isDirectionalAzimuth(azimuth) ? `<div><strong>${intl.formatMessage({ id: 'map.azimuth' })}:</strong> ${azimuth}°</div>` : ''}
            ${device.settings?.elevation != null ? `<div><strong>${intl.formatMessage({ id: 'map.elevation' })}:</strong> ${device.settings.elevation}</div>` : ''}
            ${device.lastSeenAt ? `<div><strong>${intl.formatMessage({ id: 'map.lastSeen' })}:</strong> ${new Date(device.lastSeenAt).toLocaleString(intl.locale)}</div>` : ''}
          </div>
        </div>
      `;

      const labelContent = document.createElement('span');
      labelContent.textContent = device.serial;

      popupsRef.current[device.id] = L.popup()
        .setLatLng([latitude, longitude])
        .setContent(popupContent);
      hitMarker.bindTooltip(labelContent, {
        permanent: true,
        direction: 'top',
        offset: [0, -14],
        opacity: 1,
        className: getMarkerLabelClassName(isSelected),
      });
      // A real click fires the map's 'preclick' first, which closes the open popup as if the
      // operator dismissed it; a marker click only swaps popups, via openDevicePopup.
      hitMarker.on('preclick', L.DomEvent.stopPropagation);
      hitMarker.on('click', (event: L.LeafletMouseEvent) => {
        const clickPoint = event.containerPoint;
        const candidates = devicesWithCoords.map((deviceWithCoords) => ({
          id: deviceWithCoords.id,
          point: map.latLngToContainerPoint([
            deviceWithCoords.latitude,
            deviceWithCoords.longitude,
          ]),
        }));

        const winnerId = resolveNearestDevice(clickPoint, candidates, HIT_MARKER_RADIUS);
        if (winnerId) {
          openDevicePopup(winnerId);
        }
      });
      markersRef.current[device.id] = hitMarker;
      dotMarkersRef.current[device.id] = dotMarker;
      markerLocations.push([latitude, longitude]);
    });

    // Handle showing all devices — only when the prop itself changed
    if (showAllDevices && showAllChanged && markerLocations.length > 0) {
      if (markerLocations.length > 1) {
        map.fitBounds(markerLocations, {
          padding: [24, 24],
          maxZoom: 15,
        });
      } else if (markerLocations.length === 1) {
        map.setView(markerLocations[0], 12);
      }
      hasInitializedViewRef.current = true;
      return;
    }

    // Handle individual device selection — only when the prop itself changed
    if (selectedDeviceId && selectionChanged && markersRef.current[selectedDeviceId]) {
      const marker = markersRef.current[selectedDeviceId];
      const latLng = marker.getLatLng();
      const targetZoom = Math.max(map.getZoom(), 8);
      map.setView(latLng, targetZoom, { animate: true });
      openDevicePopup(selectedDeviceId);
      hasInitializedViewRef.current = true;
      return;
    }

    // Default view — only on initial load, not on socket-driven device updates
    if (!hasInitializedViewRef.current) {
      if (markerLocations.length > 1) {
        map.fitBounds(markerLocations, { padding: [24, 24] });
      } else if (markerLocations.length === 1) {
        map.setView(markerLocations[0], 12);
      } else {
        map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      }
      hasInitializedViewRef.current = true;
    }

    // Re-open popup that was open before a socket-driven device update. If the remembered
    // device no longer exists, openDevicePopup no-ops (no matching popupsRef entry) — the
    // old popup was already closed by the teardown loop above.
    if (openPopupDeviceId && !selectionChanged && !showAllChanged) {
      openDevicePopup(openPopupDeviceId);
    }
  }, [devices, selectedDeviceId, showAllDevices, intl, openDevicePopup]);

  useEffect(() => {
    if (!isActive || !mapRef.current) {
      return;
    }

    const map = mapRef.current;
    const timeoutId = window.setTimeout(() => {
      map.invalidateSize();

      if (showAllDevices) {
        const markerLocations = Object.values(markersRef.current).map((marker) => {
          const { lat, lng } = marker.getLatLng();
          return [lat, lng] as [number, number];
        });

        if (markerLocations.length > 1) {
          map.fitBounds(markerLocations, {
            padding: [24, 24],
            maxZoom: 15,
          });
        } else if (markerLocations.length === 1) {
          map.setView(markerLocations[0], 12);
        }
      } else if (selectedDeviceId && markersRef.current[selectedDeviceId]) {
        const marker = markersRef.current[selectedDeviceId];
        const latLng = marker.getLatLng();
        const targetZoom = Math.max(map.getZoom(), 12);
        map.setView(latLng, targetZoom, { animate: true });
        openDevicePopup(selectedDeviceId);
      }
    }, 200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isActive, selectedDeviceId, showAllDevices, openDevicePopup]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedDeviceId || !onSelectedPopupClose) {
      return;
    }

    // Only one popup is open at a time, so after any operator close the selected device has none.
    const handlePopupClose = () => {
      if (!isClosingPopupsRef.current) {
        onSelectedPopupClose();
      }
    };
    map.on('popupclose', handlePopupClose);
    return () => {
      map.off('popupclose', handlePopupClose);
    };
  }, [selectedDeviceId, onSelectedPopupClose]);

  return <div ref={containerRef} className="map h-full w-full" />;
});
